import { sanitizeText, sanitizeEmail, sanitizePhone, sanitizeFileName } from './sanitize';

/**
 * Security middleware for form data validation and sanitization
 */

export interface SecureFormData {
  [key: string]: any;
}

/**
 * Sanitize common form fields
 */
export function sanitizeFormData(data: SecureFormData): SecureFormData {
  const sanitized: SecureFormData = {};

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      sanitized[key] = value;
      continue;
    }

    // Handle different field types
    if (typeof value === 'string') {
      switch (key.toLowerCase()) {
        case 'email':
          sanitized[key] = sanitizeEmail(value);
          break;
        case 'phone':
        case 'phonenumber':
        case 'phone_number':
          sanitized[key] = sanitizePhone(value);
          break;
        case 'filename':
        case 'file_name':
          sanitized[key] = sanitizeFileName(value);
          break;
        default:
          // General text sanitization
          sanitized[key] = sanitizeText(value);
      }
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeFormData(value);
    } else {
      // Keep other types as-is (numbers, booleans, etc.)
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Rate limiter for client-side operations
 * Prevents excessive API calls from a single user
 */
class RateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts: number;
  private readonly windowMs: number;

  constructor(maxAttempts: number = 5, windowMs: number = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  /**
   * Check if action is allowed for given key
   */
  isAllowed(key: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove attempts outside the time window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (recentAttempts.length >= this.maxAttempts) {
      return false;
    }

    // Add current attempt
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    
    return true;
  }

  /**
   * Get remaining attempts
   */
  getRemainingAttempts(key: string): number {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    
    return Math.max(0, this.maxAttempts - recentAttempts.length);
  }

  /**
   * Reset attempts for a key
   */
  reset(key: string): void {
    this.attempts.delete(key);
  }
}

// Export singleton instances for common use cases
export const loginRateLimiter = new RateLimiter(5, 300000); // 5 attempts per 5 minutes
export const emailRateLimiter = new RateLimiter(10, 3600000); // 10 emails per hour
export const apiRateLimiter = new RateLimiter(30, 60000); // 30 requests per minute

/**
 * Secure file upload validation
 */
export interface FileValidationOptions {
  maxSizeBytes?: number;
  allowedTypes?: string[];
  allowedExtensions?: string[];
}

export function validateFile(
  file: File,
  options: FileValidationOptions = {}
): { valid: boolean; error?: string } {
  const {
    maxSizeBytes = 10 * 1024 * 1024, // 10MB default
    allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.doc', '.docx', '.xls', '.xlsx'],
  } = options;

  // Check file size
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds ${(maxSizeBytes / 1024 / 1024).toFixed(1)}MB limit`,
    };
  }

  // Check MIME type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `File type ${file.type} is not allowed`,
    };
  }

  // Check file extension
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!allowedExtensions.includes(extension)) {
    return {
      valid: false,
      error: `File extension ${extension} is not allowed`,
    };
  }

  // Validate filename for security
  const sanitizedName = sanitizeFileName(file.name);
  if (sanitizedName === 'unnamed_file') {
    return {
      valid: false,
      error: 'Invalid filename',
    };
  }

  return { valid: true };
}

/**
 * Secure token validation
 */
export function validateToken(token: string): boolean {
  // Token should be 64 character hex string
  const tokenRegex = /^[a-f0-9]{64}$/i;
  return tokenRegex.test(token);
}

/**
 * Password strength checker
 */
export interface PasswordStrength {
  score: number; // 0-4
  feedback: string[];
  isStrong: boolean;
}

export function checkPasswordStrength(password: string): PasswordStrength {
  const feedback: string[] = [];
  let score = 0;

  // Length check
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  else feedback.push('Consider using 16+ characters for maximum security');

  // Complexity checks
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
    score++;
  } else {
    feedback.push('Use both uppercase and lowercase letters');
  }

  if (/[0-9]/.test(password)) {
    score++;
  } else {
    feedback.push('Include at least one number');
  }

  if (/[^A-Za-z0-9]/.test(password)) {
    score++;
  } else {
    feedback.push('Add special characters (!@#$%^&*)');
  }

  // Common password check
  const commonPasswords = ['password', '123456', 'qwerty', 'admin'];
  if (commonPasswords.some(common => password.toLowerCase().includes(common))) {
    score = 0;
    feedback.push('Avoid common passwords');
  }

  return {
    score: Math.min(score, 4),
    feedback,
    isStrong: score >= 4,
  };
}

/**
 * Detect potential XSS attempts in input
 */
export function detectXSS(input: string): boolean {
  const xssPatterns = [
    /<script/i,
    /javascript:/i,
    /onerror=/i,
    /onload=/i,
    /onclick=/i,
    /<iframe/i,
    /eval\(/i,
    /expression\(/i,
  ];

  return xssPatterns.some(pattern => pattern.test(input));
}

/**
 * Detect potential SQL injection attempts
 * (Note: Firestore uses NoSQL, but good to have for future API endpoints)
 */
export function detectSQLInjection(input: string): boolean {
  const sqlPatterns = [
    /(\s|^)(union|select|insert|update|delete|drop|create|alter)\s/i,
    /--/,
    /\/\*/,
    /;.*--/,
    /'\s*(or|and)\s*'?\d/i,
  ];

  return sqlPatterns.some(pattern => pattern.test(input));
}
